import {
    createStore,
    reducers,
    actions,
    dispatchEvent,
    renderState,
} from "../../../core/js/state-machine";
import {
    componentName,
    showTooltipDelay,
    maxContentWidth,
    componentClasses,
    views,
    defaultProps,
    schema,
    eventMap,
} from "../../../core/components/tooltip/tooltip";
import {
    createElement,
    DOC,
    prefix,
    createCSSClass,
    randomNumGenerator,
    waitForRender,
} from "./../../../core/js/utilities.js";
import validateInitialization from "../../../core/js/helpers/validate-initialization";
import { closeX } from "../../../core/js/helpers/built-in-icons";
import { smallMediaQueryList } from "../../../core/js/helpers/match-media";
import {
    collisionDetection,
    getPosition,
    getBoundingClientRectRelativeToOffsetParent,
    topExceed,
    bottomExceed,
} from "../../../core/js/positioning";

/**
 * Validates Tooltip required elements
 * @param {HTMLElement} Tooltip
 * @returns {Boolean}
 */
export const validateHTML = (element, mergedProps) => {
    return (
        !!DOC.querySelector(mergedProps.trigger) &&
        !!element.querySelector(`.${componentClasses.body}`)
    );
};

/**
 * Creates Tooltip
 * @param {HTMLElement} node
 * @returns Tooltip
 */
const Tooltip = (element, props) => {
    const [success, mergedProps] = validateInitialization({
        componentName,
        showTooltipDelay,
        defaultProps,
        element,
        props,
        schema,
        validateHTML,
        views,
    });
    if (!success) return;
    const buildCloseElement = () => {
        return createElement("span", {
            class: `${componentClasses.dismiss}`,
            html: closeX(),
        });
    };
    const buildPointerElement = () => {
        return createElement("div", {
            class: `${componentClasses.pointer}`,
        });
    };

    const handleTriggerKeydown = (e) => {
        // Esc
        if (e.keyCode === 27) {
            hide();
        }
    };

    const applyAccessibility = (triggerElement, element) => {
        // Aria - set trigger to be labelled by tooltip for screen reader
        const ariaDescribedById =
            triggerElement.getAttribute("aria-describedby") ||
            `${componentName.toLowerCase()}-${randomNumGenerator()}`;
        triggerElement.setAttribute("aria-describedby", ariaDescribedById);
        element.setAttribute("id", ariaDescribedById);
    };

    // Dispose
    const dispose = () => {
        clearTimeout(tooltipShowTimeout);

        //Cancel listeners and delete
        element.removeEventListener("transitionend", transitionFinished, { once: true });
        element.removeEventListener("transitioncancel", transitionFinished, { once: true });
        element.parentElement.removeChild(element);
        element = null;

        // Remove event listeners
        triggerElement.removeEventListener("mouseenter", show);
        triggerElement.removeEventListener("mouseleave", hide);
        triggerElement.removeEventListener("focus", show);
        triggerElement.removeEventListener("blur", hide);
        triggerElement.removeEventListener("keydown", handleTriggerKeydown);
        triggerElement = null;

        element[componentName] = null;
        element = null;
    };

    const handleVisibleStateChange = (visible) => {
        visible ? show() : hide();
    };

    const show = () => {
        // When we opening the tooltip, we want to have a slight delay before showing the component.
        transitionFinished();
        triggerElement.removeEventListener("focus", show);
        triggerElement.removeEventListener("mouseenter", show);
        tooltipShowTimeout = setTimeout(() => {
            renderState(views.beforeAnimation, element);
            waitForRender(element, "display", "block", 60)
                .then(() => {
                    renderState(views.show, element);
                    renderState(views.afterAnimation, element);
                    if (smallMediaQueryList()) {
                        mobileLogicTooltip();
                    }
                    updateTooltipPosition();
                    triggerElement.addEventListener("focus", show);
                    triggerElement.addEventListener("mouseenter", show);
                    dispatchEvent(eventMap[actions.show().type], element);
                })
                .catch((err) => {
                    console.warn(`${componentName} error: ${err}`);
                });
        }, showTooltipDelay);
    };

    const getTitle = () => {
        return element.querySelector(`.${componentClasses.title}`);
    };

    const getPointer = () => {
        return element.querySelector(`.${componentClasses.pointer}`);
    };

    const getBody = () => {
        return element.querySelector(`.${componentClasses.body}`);
    };

    const injectPointerElement = () => {
        if (pointerElement) return;
        element.insertBefore(buildPointerElement(), element.firstChild);
        pointerElement = getPointer();
    };

    const mobileLogicTooltip = () => {
        // Add X on mobile tooltip
        if (titleElement) {
            titleElement.after(closeElement);
        } else {
            bodyElement.insertBefore(closeElement, bodyElement.firstChild);
        }

        // Add a class for max characters content
        if (element.clientWidth > maxContentWidth) {
            renderState(views.maxContent, element);
        }
    };

    const transitionFinished = () => {
        renderState(views.afterAnimation, element);
        element.removeEventListener("transitionend", transitionFinished, { once: true });
        element.removeEventListener("transitioncancel", transitionFinished, { once: true });
    };

    const hide = () => {
        // When we closing the tooltip, we want to have a slight delay before hiding the component.
        element.addEventListener("transitionend", transitionFinished, { once: true });
        element.addEventListener("transitioncancel", transitionFinished, { once: true });
        triggerElement.addEventListener("focus", show);
        triggerElement.addEventListener("mouseenter", show);
        clearTimeout(tooltipShowTimeout);
        renderState(views.hide, element);
        // Clear tooltip pointer dynamic position class
        if (tooltipPointerDynamicClass) {
            renderState(
                {
                    classList: [`!${tooltipPointerDynamicClass}`],
                },
                pointerElement
            );
        }
        tooltipPointerDynamicClass = "";
        // Clear tooltip position class
        if (elementDynamicClass) {
            renderState(
                {
                    classList: [`!${elementDynamicClass}`],
                },
                element
            );
        }
        elementDynamicClass = "";
        dispatchEvent(eventMap[actions.hide().type], element);
    };

    const collisionDetectionMobile = (target, element, position) => {
        const targetBoundingRect = target.getBoundingClientRect();

        const isTopExceed = topExceed(targetBoundingRect, element);
        const isBottomExceed = bottomExceed(targetBoundingRect, element);

        if (position.includes("bottom")) {
            if (!isBottomExceed) {
                position = "bottom";
            } else if (!isTopExceed) {
                position = "top";
            }
        } else {
            if (!isTopExceed) {
                position = "top";
            } else if (!isBottomExceed) {
                position = "bottom";
            }
        }

        return position;
    };

    const updateTooltipPosition = () => {
        let top = 0;
        let left = 0;
        let position = mergedProps.placement;

        if (smallMediaQueryList() && element.clientWidth > maxContentWidth) {
            position = collisionDetectionMobile(triggerElement, element, position);
            ({ top, left } = getPosition(triggerElement, element, position, {
                x: 0,
                y: 8,
            }));

            const boundingBody = getBoundingClientRectRelativeToOffsetParent(element, DOC.body);
            left = Math.round(16 - boundingBody.xRelative);
        } else {
            position = collisionDetection(triggerElement, element, position);
            ({ top, left } = getPosition(triggerElement, element, position, {
                x: 8,
                y: 8,
            }));
        }

        tooltipPointerDynamicClass = `${prefix}tooltip__pointer--${position}`;
        elementDynamicClass = `${prefix}dynamic-tooltip-t${top}-l${left}`;

        renderState({ classList: [tooltipPointerDynamicClass] }, pointerElement);

        if (
            createCSSClass(`.${elementDynamicClass}`, {
                transform: `translate(${parseInt(left)}px, ${parseInt(top)}px) !important`,
            })
        ) {
            renderState(
                {
                    classList: [elementDynamicClass],
                },
                element
            );
        }
    };

    // Variables
    let triggerElement = DOC.querySelector(mergedProps.trigger);
    const bodyElement = getBody();
    let pointerElement = getPointer();
    const closeElement = buildCloseElement();
    const titleElement = getTitle();
    let tooltipShowTimeout;
    let tooltipPointerDynamicClass;
    let elementDynamicClass;

    injectPointerElement();
    applyAccessibility(triggerElement, element);

    // Add event listeners
    triggerElement.addEventListener("mouseenter", show);
    triggerElement.addEventListener("mouseleave", hide);
    triggerElement.addEventListener("focus", show);
    triggerElement.addEventListener("blur", hide);
    triggerElement.addEventListener("keydown", handleTriggerKeydown);

    const visibilityStore = createStore(reducers.boolReducer, false);
    visibilityStore.subscribe(handleVisibleStateChange);

    // Api
    const api = {
        dispose,
        element,
        show,
        hide,
    };

    // Assignment the api to the element
    element[componentName] = api;

    // Return the public api;
    return api;
};

export default Tooltip;
